import React, { type FC } from 'react'

import { useIntl } from 'react-intl'

import {
  Actions,
  Badge,
  Flex,
  GRID_VARIANT_SMALL,
  Grid,
  GridCell,
  LINK_VARIANT_HIGHLIGHT,
  Link,
  TEXT_VARIANT_LIGHT,
  TEXT_VARIANT_SMALL_LIGHT,
  Text,
  Toggle,
  TruncateAndTooltip,
} from 'mailup-web-components'

import type { ListItem } from 'Pages/UnsubscriptionCampaignsList/types'

import { isNullOrEmpty, stringOrDefault } from 'Helpers/strings'

interface ResultItemProps {
  canCreate: boolean
  item: ListItem
  onEdit: (idWorkflow: number) => () => void
  onToggleEnabled: (idWorkflow: number) => () => void
  onCopy: (idWorkflow: number) => () => void
  onSimulate: (idWorkflow: number) => () => void
  onStats: (idWorkflow: number) => () => void
  onDelete: (idWorkflow: number, name: string) => () => void
}

const ResultItem: FC<ResultItemProps> = ({
  canCreate,
  item,
  onEdit,
  onToggleEnabled,
  onCopy,
  onSimulate,
  onStats,
  onDelete,
}) => {
  const intl = useIntl()

  const menuActions = [
    {
      label: intl.formatMessage({ id: 'Load.MailupCommon.Edit' }),
      separator: false,
      onClick: onEdit(item.Id),
    },

    canCreate && !item.Draft
      ? {
          label: intl.formatMessage({ id: 'Load.MailupCommon.Copy' }),
          separator: false,
          onClick: onCopy(item.Id),
        }
      : undefined,

    !item.Draft
      ? {
          label: intl.formatMessage({ id: 'Load.MailupCommon.ActionSimulate' }),
          separator: false,
          onClick: onSimulate(item.Id),
        }
      : undefined,
    !item.Draft && item.CountEmailMessages > 0
      ? {
          label: intl.formatMessage({
            id: 'Load.MailupCommon.ActionStatistics',
          }),
          separator: false,
          onClick: onStats(item.Id),
        }
      : undefined,
    {
      label: intl.formatMessage({ id: 'Load.MailupCommon.Delete' }),
      separator: true,
      onClick: onDelete(item.Id, item.Name),
    },
  ].filter((o) => o !== undefined)

  const idAndNotes = `${intl.formatMessage({ id: 'Load.MailupCommon.Id' })} ${
    item.Id
  } ${!isNullOrEmpty(item.Notes) ? ` - ${item.Notes}` : ''}`

  return (
    <Grid cols={12} variant={GRID_VARIANT_SMALL}>
      <GridCell span={3}>
        <Flex flexDirection="column" gap="4px">
          <Link
            variant={LINK_VARIANT_HIGHLIGHT}
            data-mu-test="item-name"
            onClick={onEdit(item.Id)}
          >
            <TruncateAndTooltip text={item.Name} />
          </Link>
          <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
            <TruncateAndTooltip text={idAndNotes} />
          </Text>
        </Flex>
      </GridCell>
      <GridCell span={3}>
        <Flex flexDirection="column" gap="4px">
          <Text variant={TEXT_VARIANT_LIGHT}>
            {intl.formatMessage({
              id: 'Load.AutomationWorkflow.UnsubscriptionCampaignTitle',
            })}
          </Text>

          {item.CountEmailMessages > 0 && (
            <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
              {item.CountEmailMessages}{' '}
              {intl.formatMessage({
                id: 'Load.AutomationWorkflow.CountMessagesDescription',
              })}
            </Text>
          )}
        </Flex>
      </GridCell>
      <GridCell span={2}>{item.LastModifiedDate}</GridCell>
      <GridCell span={2}>
        {stringOrDefault(item.LastActivationDate, '-')}
      </GridCell>
      <GridCell span={2}>
        <Flex alignItems="center" justifyContent="space-between">
          {item.Draft ? (
            <Badge
              kind={'secondary'}
              text={intl.formatMessage({
                id: 'Load.AutomationWorkflow.DraftLabel',
              })}
            />
          ) : (
            <Toggle
              checked={item.Enabled}
              onChange={onToggleEnabled(item.Id)}
              name={`statusToggle-${item.Id}`}
              value="true"
            />
          )}

          <Actions items={menuActions} />
        </Flex>
      </GridCell>
    </Grid>
  )
}

export default ResultItem
